import {
  ActionName,
  CategoryName,
  DisplayModeName,
  IContext,
  IFormApi,
  IOptionSchema,
  TDatabaseId,
  TDatabaseRecord,
  TDatasetName,
} from '@infotech-soe/metaschema';
import * as common from '@metarhia/common';
import * as dateUtils from 'date-fns';

import {
  getLogger,
  InjectDebugLogger,
} from '../../../framework/logging/decorators/InjectDebugLogger';
import IApiProvider, {
  TActionArgsValues,
} from '../../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import IIntlService from '../../intl/IIntlService';
import IntlService from '../../intl/IntlService';

class EFormError extends Error {
  public info?: string;

  constructor(message: string) {
    super(message);
    this.info = message;
  }
}
@InjectDebugLogger('form-api')
class FormApi implements IFormApi {
  private readonly apiProvider: IApiProvider;
  private readonly intlService: IIntlService;
  constructor(
    private readonly vars: Map<string, any>,
    private processState: Record<string, any>,
    private readonly application: string
  ) {
    this.apiProvider = new MerpsApiProvider();
    this.intlService = new IntlService();
  }

  public dateUtils = dateUtils;

  public getApplication(): string {
    return this.application;
  }
  public getVar(varName: string): any {
    return this.vars.get(varName);
  }
  public setVar(varName: string, value: any) {
    this.vars.set(varName, value);
  }

  public getProcessState(varName: string): any {
    return this.processState[varName];
  }
  public setProcessState(varName: string, value: any): void {
    this.processState[varName] = value;
  }

  public async executeAction(
    action: ActionName,
    context: IContext,
    args: TActionArgsValues,
    categoryName?: CategoryName
  ): Promise<any> {
    return this.apiProvider.actions.executeAction(
      action,
      context,
      args,
      categoryName
    );
  }

  public async getOptionValue(name: string): Promise<any> {
    try {
      const option = await this.apiProvider.options.getOption(name);
      return option.type === 'date' && option.value
        ? new Date(option.value)
        : option.value;
    } catch {
      throw new Error(`Option not found: ${name}`);
    }
  }

  public getOptionSchema(name: string): Promise<IOptionSchema> {
    return this.apiProvider.schemas.getOptionSchema(name);
  }

  public async getDisplays(category: CategoryName): Promise<DisplayModeName[]> {
    return (await this.apiProvider.schemas.getDisplayModeSchema(category)).map(
      e => e.name
    );
  }

  public getState(): Record<string, any> {
    return this.processState;
  }

  public getVars(): Map<string, any> {
    return this.vars;
  }

  public getById(
    source: string,
    id: TDatabaseId,
    field?: string
  ): Promise<Record<string, any>> {
    return this.apiProvider.db.getById(source, id, field);
  }

  public async getByPosParams(
    category: string,
    displayMode: string,
    params: Record<string, any>
  ): Promise<Record<TDatasetName, TDatabaseRecord>> {
    return this.apiProvider.displayModes.getByPosParams(
      category,
      displayMode,
      params
    );
  }

  public today(): Date {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  public toDate(s: string | null | undefined): Date | null {
    return s === null || s === undefined || s === '' ? null : new Date(s);
  }

  public async throwError(
    resId: string,
    substitution?: Record<string, any>
  ): Promise<Error> {
    throw new EFormError(
      await this.intlService.getCategoryMessage(
        this.vars.get('formCategory'),
        resId,
        substitution
      )
    );
  }

  public async getResource(
    resId: string,
    substitution?: Record<string, any>,
    category?: CategoryName
  ): Promise<string> {
    return this.intlService.getCategoryMessage(
      category ?? this.vars.get('formCategory'),
      resId,
      substitution
    );
  }

  public log(...args: any): void {
    const logger = getLogger(FormApi);

    logger(args);
  }

  public get common() {
    return common;
  }
}

export default FormApi;
